import React, { useState, useCallback, createContext } from 'react';
// Deep path, not the barrel — see the note in Toast.jsx (barrel -> redux -> ToastManager cycle).
import { AppBox } from './Design_System/Layout/Box';
import Toast from './Toast';

const ToastManager = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const isDarkTheme = () => {
    return document.documentElement.classList.contains('dark');
  };

  const normalizeMessage = (input) => {
    try {
      if (input === null || input === undefined) return '';
      if (
        typeof input === 'object' &&
        input &&
        input.__type === 'formula_expression'
      ) {
        return input;
      }
      if (
        typeof input === 'object' &&
        input &&
        input.__type === 'pricing_logs'
      ) {
        return input;
      }
      if (
        typeof input === 'object' &&
        input &&
        input.__type === 'row_validation_error'
      ) {
        return input;
      }
      if (
        typeof input === 'object' &&
        input &&
        input.__type === 'save_result'
      ) {
        return input;
      }
      if (
        typeof input === 'object' &&
        input &&
        input.__type === 'execution_log'
      ) {
        return input;
      }
      if (typeof input === 'string') return input;
      if (typeof input === 'number' || typeof input === 'boolean')
        return String(input);

      if (input instanceof Error) return input.message || String(input);

      const resp = input.response || input.data || input;
      const candidates = [
        resp?.data?.message,
        resp?.data?.error,
        resp?.data?.detail,
        resp?.message,
        resp?.error,
        resp?.detail,
      ];

      for (const c of candidates) {
        if (!c) continue;
        if (Array.isArray(c)) {
          const mapped = c
            .map((item) =>
              typeof item === 'string'
                ? item
                : item?.message || item?.detail || JSON.stringify(item),
            )
            .filter(Boolean);
          if (mapped.length) return mapped.join(', ');
        } else if (typeof c === 'object') {
          const vals = Object.values(c)
            .flat()
            .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
          if (vals.length) return vals.join(', ');
        } else if (typeof c === 'string') {
          return c;
        }
      }

      if (Array.isArray(input)) {
        return input;
      }

      if (typeof input === 'object' && input !== null) {
        if (Array.isArray(input.details)) return input;
      }

      return JSON.stringify(input, (_, v) => (v === undefined ? null : v));
    } catch {
      return String(input);
    }
  };

  const coerceOptions = (arg2, duration, pinned) => {
    if (typeof arg2 === 'object' && arg2 !== null) {
      return {
        type: arg2.type || 'info',
        duration: arg2.duration ?? 4000,
        pinned: arg2.pinned ?? false,
      };
    }
    return {
      type: arg2 || 'info',
      duration: duration ?? 4000,
      pinned: pinned ?? false,
    };
  };

  const showToast = useCallback(
    (message, typeOrOptions = 'info', duration, pinned) => {
      const {
        type,
        duration: dur,
        pinned: pin,
      } = coerceOptions(typeOrOptions, duration, pinned);
      const id = Date.now() + Math.random();
      const newToast = {
        id,
        message: normalizeMessage(message),
        type,
        duration: dur,
        pinned: pin,
      };
      if (
        typeof typeOrOptions === 'object' &&
        typeOrOptions.onVisibilityChange
      ) {
        newToast.onVisibilityChange = typeOrOptions.onVisibilityChange;
      }
      if (typeof typeOrOptions === 'object' && typeOrOptions.onUnpin) {
        newToast.onUnpin = typeOrOptions.onUnpin;
      }
      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [],
  );

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message, duration = 4000, pinned = false) => {
      return showToast(message, 'success', duration, pinned);
    },
    [showToast],
  );

  const showError = useCallback(
    (message, duration = 6000, pinned = false) => {
      return showToast(message, { type: 'error', duration, pinned });
    },
    [showToast],
  );

  const showWarning = useCallback(
    (message, duration = 5000, pinned = false) => {
      return showToast(message, { type: 'warning', duration, pinned });
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message, duration = 4000, pinned = false) => {
      return showToast(message, { type: 'info', duration, pinned });
    },
    [showToast],
  );

  const togglePinToast = useCallback((id, pinned) => {
    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, pinned } : toast)),
    );
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const clearPinnedToasts = useCallback(() => {
    setToasts((prev) => prev.filter((t) => !t.pinned));
  }, []);

  const toastContext = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    togglePinToast,
    clearAllToasts,
    clearPinnedToasts,
  };

  return (
    <ToastContext.Provider value={toastContext}>
      {children}
      <AppBox
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast, index) => (
          <AppBox
            key={toast.id}
            sx={{
              marginBottom: index > 0 ? '8px' : 0,
              pointerEvents: 'auto',
              transform: `translateY(${-index * 8}px)`,
              transition: 'transform 0.3s ease',
            }}
          >
            <Toast
              open={true}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              pinned={toast.pinned}
              isDarkTheme={isDarkTheme()}
              onClose={() => removeToast(toast.id)}
              onVisibilityChange={(visible) => {
                if (toast.onVisibilityChange) {
                  toast.onVisibilityChange(visible);
                } else {
                  //console.log('No onVisibilityChange handler for toast id);
                }
              }}
              onTogglePin={(pinned) => {
                togglePinToast(toast.id, pinned);
                if (pinned === false && toast.onUnpin) toast.onUnpin();
              }}
            />
          </AppBox>
        ))}
      </AppBox>
    </ToastContext.Provider>
  );
};

export const ToastContext = createContext();

export default ToastManager;

export const toast = {
  showToast: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  togglePinToast: () => {},
  clearAll: () => {},
  clearPinned: () => {},
};

const bridgeStack = [];

// True once a <ToastBridge> is mounted; until then `toast` above is a no-op.
export const isToastReady = () => bridgeStack.length > 0;

const bindToast = (ctx) => {
  toast.showToast = ctx.showToast;
  toast.showSuccess = ctx.showSuccess;
  toast.showError = ctx.showError;
  toast.showWarning = ctx.showWarning;
  toast.showInfo = ctx.showInfo;
  toast.togglePinToast = ctx.togglePinToast;
  toast.clearAll = ctx.clearAllToasts;
  toast.clearPinned = ctx.clearPinnedToasts;
};

const ToastBridgeInner = ({ children }) => {
  const ctx = React.useContext(ToastContext);
  React.useEffect(() => {
    if (!ctx) return;
    bridgeStack.push(ctx);
    bindToast(ctx);
    return () => {
      const idx = bridgeStack.indexOf(ctx);
      if (idx !== -1) bridgeStack.splice(idx, 1);
      const top = bridgeStack[bridgeStack.length - 1];
      if (top) bindToast(top);
    };
  }, [ctx]);
  return children;
};

export const ToastBridge = ({ children }) => (
  <ToastManager>
    <ToastBridgeInner>{children}</ToastBridgeInner>
  </ToastManager>
);
